import NavigationButtons from "../components/NavigationButtons";
import { useAuthState } from "../hooks/useAuth";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen, Layers, StickyNote } from "lucide-react";
import ApprovedTagChips from "../components/ApprovedTagChips";
import Spinner from "../components/Spinner";
import { articlesAPI, tagsAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import { formatReadingEstimate } from "../utils/readingTime";
import {
  getPublicFlashcardsPath,
  getPublicLessonPath,
  getPublicNotesPath,
  getTutorialLessonSubject,
  isTutorialLesson,
  rememberTutorialLessonId,
  sortTutorialLessons,
  TUTORIAL_ACTIONS,
} from "../constants/tutorialContent";
import "./Tutorial.css";
import "./TutorialSubjects.css";

const ACTION_CONFIG = {
  [TUTORIAL_ACTIONS.READ]: {
    title: "Pick a subject to start reading",
    subtitle: "Choose one public tutorial domain and jump straight into the lesson.",
    icon: BookOpen,
  },
  [TUTORIAL_ACTIONS.NOTES]: {
    title: "Pick a subject for notes",
    subtitle: "Choose the domain whose notes you want to review or continue.",
    icon: StickyNote,
  },
  [TUTORIAL_ACTIONS.FLASHCARDS]: {
    title: "Pick a subject for flashcards",
    subtitle: "Choose the domain whose flashcards you want to study.",
    icon: Layers,
  },
};

const resolveAction = (rawAction) => {
  if (rawAction === TUTORIAL_ACTIONS.NOTES) return TUTORIAL_ACTIONS.NOTES;
  if (rawAction === TUTORIAL_ACTIONS.FLASHCARDS) return TUTORIAL_ACTIONS.FLASHCARDS;
  return TUTORIAL_ACTIONS.READ;
};

const getTargetPath = (action, lesson) => {
  if (action === TUTORIAL_ACTIONS.NOTES) {
    return getPublicNotesPath(lesson);
  }

  if (action === TUTORIAL_ACTIONS.FLASHCARDS) {
    return getPublicFlashcardsPath(lesson);
  }

  return getPublicLessonPath(lesson);
};

const getLessonTags = (lesson) =>
  Array.isArray(lesson?.tags) ? lesson.tags : [];

const shuffleSubjects = (lessons = []) => {
  const shuffled = [...lessons];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [
      shuffled[swapIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const TutorialSubjects = () => {
  const navigate = useNavigate();
  const { user } = useAuthState();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToasts();
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [approvedTags, setApprovedTags] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);

  const action = resolveAction(searchParams.get("action"));
  const selectedTag = searchParams.get("tag") || "";
  const actionConfig = ACTION_CONFIG[action];
  const HeaderIcon = actionConfig.icon;

  useEffect(() => {
    let isMounted = true;

    const loadSubjects = async () => {
      try {
        const [response, tagsResponse] = await Promise.all([
          articlesAPI.getPublicArticles(),
          tagsAPI.getApprovedTags("lesson").catch(() => ({ data: { tags: [] } })),
        ]);
        const lessons = Array.isArray(response.data) ? response.data : [];
        const tutorialLessons = shuffleSubjects(
          sortTutorialLessons(
            lessons.filter((lesson) => isTutorialLesson(lesson)),
          ),
        );

        if (!isMounted) return;
        setSubjects(tutorialLessons);
        setApprovedTags(tagsResponse.data?.tags || []);
      } catch {
        if (!isMounted) return;
        addToast("Failed to load tutorial subjects", "error-warning");
        navigate("/tutorial", { replace: true });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSubjects();

    return () => {
      isMounted = false;
    };
  }, [addToast, navigate]);

  const availableTags = approvedTags.filter((tag) =>
    subjects.some((lesson) => getLessonTags(lesson).includes(tag.slug)),
  );
  const primaryTags = availableTags.slice(0, 7);
  const otherTags = availableTags.slice(primaryTags.length);
  const hasOtherTags = otherTags.length > 0;
  const filteredSubjects = useMemo(
    () =>
      selectedTag
        ? subjects.filter((lesson) => getLessonTags(lesson).includes(selectedTag))
        : subjects,
    [selectedTag, subjects],
  );
  const otherSubjects = useMemo(
    () =>
      selectedTag
        ? subjects.filter((lesson) => !getLessonTags(lesson).includes(selectedTag))
        : [],
    [selectedTag, subjects],
  );
  const visibleSubjects = useMemo(
    () => filteredSubjects,
    [filteredSubjects],
  );

  if (loading) {
    return <Spinner visible={true} />;
  }

  const handleTagClick = (tagSlug) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("action", action);
    nextParams.set("view", "all");

    if (selectedTag === tagSlug) {
      nextParams.delete("tag");
    } else {
      nextParams.set("tag", tagSlug);
    }

    setSearchParams(nextParams);
  };
  const navigateLesson = (lesson, targetAction = TUTORIAL_ACTIONS.READ) => {
    rememberTutorialLessonId(lesson._id);
    navigate(getTargetPath(targetAction, lesson));
  };
  const handleCardKeyDown = (event, lesson) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    navigateLesson(lesson);
  };
  const renderSubjectCard = (lesson) => (
    <article
      key={lesson._id}
      className="tutorial-subject-card"
      role="button"
      tabIndex={0}
      onClick={() => navigateLesson(lesson)}
      onKeyDown={(event) => handleCardKeyDown(event, lesson)}
    >
      <div className="tutorial-subject-card-topline">
        <div className="tutorial-subject-card-meta">
          <span className="tutorial-subject-chip">
            {getTutorialLessonSubject(lesson)}
          </span>
          {getLessonTags(lesson).length > 0 && (
            <ApprovedTagChips
              tags={approvedTags.filter((tag) =>
                getLessonTags(lesson).includes(tag.slug),
              )}
              selectedSlugs={getLessonTags(lesson)}
              size="small"
              className="tutorial-subject-card-tags"
              interactive={false}
            />
          )}
          <span className="tutorial-subject-card-reading-estimate">
            {formatReadingEstimate(lesson)}
          </span>
        </div>
        <div className="tutorial-subject-card-quick-actions">
          <button
            type="button"
            className="tutorial-subject-card-icon-button tutorial-subject-card-notes-button"
            title="Notes"
            aria-label="Notes"
            onClick={(event) => {
              event.stopPropagation();
              navigateLesson(lesson, TUTORIAL_ACTIONS.NOTES);
            }}
          >
            <StickyNote size={18} />
          </button>
          <button
            type="button"
            className="tutorial-subject-card-icon-button tutorial-subject-card-flashcards-button"
            title="Flashcards"
            aria-label="Flashcards"
            onClick={(event) => {
              event.stopPropagation();
              navigateLesson(lesson, TUTORIAL_ACTIONS.FLASHCARDS);
            }}
          >
            <Layers size={18} />
          </button>
        </div>
      </div>
      <h2 className="tutorial-subject-card-title">
        {lesson.author || "Asasiasa"}
      </h2>
      <p className="tutorial-subject-card-body">
        {lesson.body || "Public tutorial lesson"}
      </p>
    </article>
  );

  return (
    <div className="tutorial-container tutorial-subjects-container">
      {user && <NavigationButtons guestRedirectPath="/signup" />}
      <div className="tutorial-subjects-header">
        <div className="tutorial-subjects-header-icon">
          <HeaderIcon size={28} />
        </div>
        <h1 className="tutorial-title tutorial-subjects-title">
          {actionConfig.title}
        </h1>
        <p className="tutorial-subtitle tutorial-subjects-subtitle">
          {actionConfig.subtitle}
        </p>
      </div>

      {availableTags.length > 0 && (
        <div className="tutorial-subjects-tags">
          <div className="tutorial-subjects-tags-primary-row">
            <ApprovedTagChips
              tags={hasOtherTags ? primaryTags : availableTags}
              selectedSlugs={selectedTag ? [selectedTag] : []}
              onTagClick={handleTagClick}
            />
            {hasOtherTags && (
              <button
                type="button"
                className="approved-tag-chip tutorial-subjects-other-tags-button"
                onClick={() => setShowAllTags((current) => !current)}
                aria-expanded={showAllTags}
              >
                Other tags
              </button>
            )}
          </div>
          {hasOtherTags && showAllTags && (
            <div className="tutorial-subjects-tags-expanded">
              <ApprovedTagChips
                tags={otherTags}
                selectedSlugs={selectedTag ? [selectedTag] : []}
                onTagClick={handleTagClick}
              />
            </div>
          )}
        </div>
      )}

      {visibleSubjects.length ? (
        <div className="tutorial-subjects-grid">
          {visibleSubjects.map(renderSubjectCard)}
        </div>
      ) : (
        <div className="tutorial-lesson-empty-state tutorial-subjects-empty-state">
          <p>No public tutorial subjects are available yet</p>
        </div>
      )}

      {otherSubjects.length > 0 && (
        <section className="tutorial-subjects-secondary-section">
          <h2 className="tutorial-subjects-secondary-title">Other lessons</h2>
          <div className="tutorial-subjects-grid tutorial-subjects-grid-secondary">
            {otherSubjects.map(renderSubjectCard)}
          </div>
        </section>
      )}
    </div>
  );
};

export default TutorialSubjects;
